import umlCanvasEngine from '../UmlCanvasEngine';
import umlConnectorEngine from '../UmlConnectorEngine';
import umlShapeSerializer from '../UmlShapeSerializer';

/**
 * Turns undo snapshots back into live canvas state. All type-specific
 * restore knowledge lives here, deliberately kept OUT of UmlUndoEngine so
 * the engine stays a dumb stack (see UmlUndoEngine.js).
 */
class UmlUndoRestorer {
    /**
     * @param {{type: string, data: object}} undoData - result of UmlUndoEngine.undo()
     */
    restore(undoData) {
        if (!undoData) {
            return;
        }
        if (undoData.type === 'delete') {
            this._restoreDelete(undoData.data);
        } else if (undoData.type === 'hiddenItems') {
            this._restoreHiddenItems(undoData.data);
        }
    }

    /**
     * Restores a deleted shape (+ its nested children, already inside the
     * shape object) and every connector that was attached to it or to one
     * of its nested children.
     *
     * WHY shape.fromPlainShape/fromPlainConnector: createSnapshot deep-clones
     * via JSON.parse(JSON.stringify(...)), which strips class identity —
     * data.shape/data.connectors are plain objects, not UmlPackageShape/
     * UmlClassShape/UmlConnector instances. umlCanvasEngine.restoreShape() and
     * umlConnectorEngine.addConnector() both expect real instances, same
     * reason UmlShapeSerializer exists for schema loads (see its own doc
     * comment).
     *
     * ORDER MATTERS: shape is added back first, connectors after — a
     * connector's fromShapeId/toShapeId must already exist on the canvas
     * before the connector can be meaningfully re-added.
     * @param {{shape: object, connectors: Array}} data - plain-JSON snapshot
     */
    _restoreDelete(data) {
        const shape = umlShapeSerializer.fromPlainShape(data.shape);
        if (!shape) {
            return;
        }
        umlCanvasEngine.restoreShape(shape); // NOT addShape — restoreShape preserves the original zIndex/parentId instead of bringing it to front

        data.connectors.forEach(function restoreConnector(connectorPlain) {
            const connector = umlShapeSerializer.fromPlainConnector(connectorPlain);
            if (connector) {
                umlConnectorEngine.addConnector(connector);
            }
        });
    }

    /**
     * Restores viewConfig.userHiddenItems to its exact pre-action snapshot
     * (hide/unhide via setItemHidden, or Unhide All via clearHiddenItems) — no
     * deep-clone/serializer needed here, unlike _restoreDelete, since this
     * data is just a plain array of ids, no class-identity to lose.
     *
     * Any UmlHiddenItemsDlg that's currently open updates automatically —
     * umlCanvasEngine.restoreHiddenItems calls notify(), and the dialog
     * already subscribes to umlCanvasEngine (see UmlHiddenItemsDlg.jsx
     * componentDidMount), same as every other canvas-state change.
     * @param {{previous: string[]}} data
     */
    _restoreHiddenItems(data) {
        umlCanvasEngine.restoreHiddenItems(data.previous);
    }
}

const umlUndoRestorer = new UmlUndoRestorer();
export default umlUndoRestorer;
